import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "明星动态", to: "/category/star-latest" },
  { label: "网络热议", to: "/category/net-buzz" },
  { label: "热门视频", to: "/category/hot-review" },
  { label: "深度解析", to: "/category/deep-insight" },
  { label: "专题", to: "/#topics" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const runSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
      isActive
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b transition-all ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="flex h-16 w-full items-center gap-4 px-4 md:px-6">
          {/* Brand */}
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2 cursor-pointer"
            aria-label="黑料视频 首页"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-movie-2-fill text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
                黑料视频
              </span>
              <span className="hidden text-[10px] tracking-widest text-foreground-500 sm:block">
                HEILIAO VIDEO
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="ml-2 hidden items-center gap-6 lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {/* Desktop search */}
            <form
              onSubmit={runSearch}
              className="hidden items-center rounded-full border border-background-200 bg-background-100 px-3 py-1.5 md:flex"
            >
              <i className="ri-search-line text-sm text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索明星 / 事件 / 视频"
                className="w-36 bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 xl:w-48"
              />
            </form>

            {/* Mobile search shortcut */}
            <Link
              to="/search"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer md:hidden"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>

            {/* Hamburger */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer lg:hidden"
              aria-label="打开菜单"
            >
              <i className="ri-menu-3-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden ${
          drawerOpen ? "" : "pointer-events-none"
        }`}
        aria-hidden={!drawerOpen}
      >
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-foreground-950/50 transition-opacity ${
            drawerOpen ? "opacity-100" : "opacity-0"
          }`}
        ></div>
        <aside
          className={`absolute right-0 top-0 flex h-full w-72 max-w-[80%] flex-col bg-background-50 transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex h-16 items-center justify-between px-5">
            <span className="font-heading text-lg font-black text-foreground-950">
              黑料视频
            </span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 hover:bg-background-200 cursor-pointer"
              aria-label="关闭菜单"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <nav className="flex flex-col gap-1 px-3 pt-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-3 text-sm font-medium cursor-pointer ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-800 hover:bg-background-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto border-t border-background-200 p-4">
            <Link
              to="/contact"
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3 text-sm font-medium text-background-50 cursor-pointer hover:bg-primary-600"
            >
              <i className="ri-mail-send-line"></i>
              联系我们
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}